import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import * as yup from 'yup';
import { classnames } from 'Utils/classnames/classnames.utils';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { t } from 'Src/i18n';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Button } from 'Components/frameworks/button/Button';
import { FormFeedback } from 'Components/frameworks/form-feedback/FormFeedback';
// THIS SPECIFIC CODE IS NOT A GOOD PRACTICE, REFUSE ANY DEMANDS THAT WOULD ADD MORE CODE LIKE THIS https://github.com/ClubMediterranee/cm-b2c/issues/21598
const PROMO_CODE_MAX_LENGTH = 15;
const PROMO_CODE_VALIDATOR = yup.string().max(PROMO_CODE_MAX_LENGTH).required();
export type PromoCodeStatus =
  | 'SUCCESS'
  | 'AUTHORIZATION_ERROR'
  | 'INVALID_PROMO_CODE'
  | 'WRONG_PROMO_CODE'
  | 'PROMO_CODE_QUOTA_EXCEEDED'
  | 'PROMO_CANNOT_BE_APPLIED'
  | 'BEST_PROMOTION_ALREADY_APPLIED'
  | 'COMMERCIAL_OFFER_ALREADY_USED'
  | 'ALREADY_APPLIED_PROMOTION'
  | 'REQUIRED_IDENTIFIED_ATTENDEES'
  | null;
const promoCodeErrorToCmsKey = {
  SUCCESS: '',
  AUTHORIZATION_ERROR: 'customerNotEligible',
  INVALID_PROMO_CODE: 'invalidPromoCode',
  WRONG_PROMO_CODE: 'wrongPromoCode',
  PROMO_CODE_QUOTA_EXCEEDED: 'quotaExceeded',
  PROMO_CANNOT_BE_APPLIED: 'criteriaNotApplicable',
  BEST_PROMOTION_ALREADY_APPLIED: 'bestPromotionAlreadyApplied',
  COMMERCIAL_OFFER_ALREADY_USED: 'alreadyUsed',
  ALREADY_APPLIED_PROMOTION: 'alreadyAppliedOnProposal',
  REQUIRED_IDENTIFIED_ATTENDEES: 'requiredIdentifiedAttendees',
};
type PromoCodeFormProps = {
  selectedVouchers: Array<string>;
  shouldDisplayIsUnfolded: boolean;
  promoCodeStatus: PromoCodeStatus;
  setPromoCodeStatus: (status: PromoCodeStatus) => void;
};

const getPromocodeFeedbackMessage = (
  valuesPromoCode: string,
  isPromoCodeRequestOnError: boolean,
  promoCodeStatus: PromoCodeStatus,
  selectedVouchers: Array<string>, // eslint-disable-next-line max-params
) => {
  if (isPromoCodeRequestOnError && promoCodeStatus !== null) {
    return t(
      `booking.priceDetails.promoCode.${promoCodeErrorToCmsKey?.[promoCodeStatus] ?? 'error'}`,
    );
  }

  if (selectedVouchers.length > 0) {
    return t('booking.priceDetails.promoCode.success');
  }

  return '';
};

export const PromoCodeForm = ({
  selectedVouchers,
  shouldDisplayIsUnfolded,
  promoCodeStatus,
  setPromoCodeStatus,
}: PromoCodeFormProps) => {
  const { setFieldError, submitForm, values, errors } = useFormikContext();
  const isPromoCodeRequestOnError = promoCodeStatus !== 'SUCCESS' && promoCodeStatus !== null;
  const isPromoCodeRequestOnSuccess = promoCodeStatus === 'SUCCESS';
  const { trackClickEvent } = useTrackEvent();
  const alreadyHasVoucher = selectedVouchers.length > 0;
  const feedbackMessage = getPromocodeFeedbackMessage(
    values.promoCode,
    isPromoCodeRequestOnError,
    promoCodeStatus,
    selectedVouchers,
  );
  useEffect(() => {
    if (isPromoCodeRequestOnError) {
      setFieldError('promoCode', 'Request failed');
    }
  }, [isPromoCodeRequestOnError]);
  return (
    <div>
      {shouldDisplayIsUnfolded && (
        <FormikInput
          dataTestId="togglePromoCodeField"
          className="mt-20"
          label={
            alreadyHasVoucher
              ? t('booking.priceDetails.promoCode.triggerAnotherText')
              : t('booking.priceDetails.promoCode.triggerText')
          }
          name="isUnfolded"
          type="checkbox"
          onClick={() => {
            if (!values.isUnfolded) {
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_SUMMARY, {
                detail_click: 'promotional_code',
              });
            }

            if (promoCodeStatus !== null) {
              setPromoCodeStatus(null);
              setFieldError('promoCode', '');
            }
          }}
        />
      )}
      <div className="PromoCode-trigger inline-block text-right"></div>
      {values.isUnfolded && (
        <div>
          <div className="-mt-10 flex items-end gap-15">
            <div className="md:w-1/3" data-selector="PromoCode-inputContainer">
              <FormikInput
                className={classnames(
                  'air360-replay-ignore h-48 w-full appearance-none rounded-pill border border-lightGrey bg-white px-20 py-10 text-14 leading-normal text-black outline-none transition-all duration-250 ease-in focus:border-black',
                  {
                    'border-success': isPromoCodeRequestOnSuccess,
                    'border-red': errors.promoCode,
                  },
                )}
                hideErrorMessage
                label={t('booking.priceDetails.promoCode.label')}
                placeholder={t('booking.priceDetails.promoCode.fieldLabel')}
                maxLength={PROMO_CODE_MAX_LENGTH}
                name="promoCode"
                type="text"
                validate={PROMO_CODE_VALIDATOR}
                dataTestId="PromoCodeForm-input"
              />
            </div>

            <Button className="mt-10" data-testid="PromoCodeForm-submit" onClick={submitForm}>
              {t('booking.priceDetails.promoCode.apply')}
            </Button>
          </div>
        </div>
      )}
      <div>
        {promoCodeStatus !== null && !!feedbackMessage && (
          <FormFeedback message={feedbackMessage} isError={isPromoCodeRequestOnError} />
        )}
      </div>
    </div>
  );
};
